import { type MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import { isNotDefined, TypedBroadcastChannel } from '@enterprise/core';

import type { NthCalculatorBroadcastMessage } from '@feat/reports/types/nth-calculator-broadcast-message.type';

import { useNthCalculatorStore } from '@components/nth-calculator/stores/nth-calculator-store';
import { NTH_CALCULATOR_CHANNEL } from '@feat/reports/components/utilities/nth-calculator-broadcast';
import { useMergePurgeStore } from '@feat/reports/context/hooks/merge-purge-store.hook';

const MIN_PERCENT = 20;
const MAX_PERCENT = 80;
const DEFAULT_LEFT_PERCENT = 60;

export const useMergePurgeBodyConfiguration = () => {
  const [leftPercent, setLeftPercent] = useState(DEFAULT_LEFT_PERCENT);
  const [isResizing, setIsResizing] = useState(false);

  const isOpen = useMergePurgeStore(({ isNthCalculatorOpen }) => {
    return isNthCalculatorOpen;
  });
  const setIsNthCalculatorOpen = useMergePurgeStore(({ setIsNthCalculatorOpen }) => {
    return setIsNthCalculatorOpen;
  });
  const setIsPopout = useNthCalculatorStore(({ setIsPopout }) => {
    return setIsPopout;
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);

  const onDividerMouseDown = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    if (!isOpen) {
      return;
    }

    isDragging.current = true;

    setIsResizing(true);

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [isOpen]);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || isNotDefined(containerRef.current)) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const rawPercent = ((e.clientX - rect.left) / rect.width) * 100;

    setLeftPercent(Math.min(Math.max(rawPercent, MIN_PERCENT), MAX_PERCENT));
  }, []);

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) {
      return;
    }

    isDragging.current = false;

    setIsResizing(false);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    const channel = new TypedBroadcastChannel<NthCalculatorBroadcastMessage>(NTH_CALCULATOR_CHANNEL, (message) => {
      if (message.type === 'return-to-panel') {
        setIsNthCalculatorOpen(true);
      } else if (message.type === 'draft-updated') {
        void useNthCalculatorStore.persist.rehydrate();
      }
    });

    return () => {
      channel.close();
    };
  }, [setIsNthCalculatorOpen]);

  useEffect(() => {
    if (!isOpen) {
      setLeftPercent(DEFAULT_LEFT_PERCENT);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIsPopout(false);
    }
  }, [isOpen, setIsPopout]);

  return { containerRef, isOpen, isResizing, leftPercent, onDividerMouseDown, setIsNthCalculatorOpen };
};
